import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import IconPicker from './IconPicker';
import { CourseIcon, ICONS } from './iconLibrary';
import { updateCourse, deleteCourse } from '../../services/adminService';
import { confirmDialog } from '../Common/confirm';
import toast from 'react-hot-toast';

/* Дизайн из Claude Design (Course Settings Modal.dc.html), на токенах темы.
   Превью-прямоугольник = фон-узор карточек редактора (ShellCover). Цвет — только
   линия-ползунок тона + hex. Видимость/кураторы/защита — демо. */

export const EXAMS = [{ v: 'ЕГЭ', l: 'ЕГЭ' }, { v: 'ОГЭ', l: 'ОГЭ' }, { v: '', l: 'Без типа' }];
const VIS = [{ v: 'pub', l: 'Опубликован' }, { v: 'draft', l: 'Черновик' }];
const CURATORS = [
  { key: 'lapin', name: 'Егор Лапин', role: 'куратор · 42 проверки за неделю', initials: 'ЕЛ' },
  { key: 'sokolov', name: 'Артём Соколов', role: 'главный администратор', initials: 'АС' },
  { key: 'volkova', name: 'Дарья Волкова', role: 'куратор · группы ОГЭ', initials: 'ДВ' },
];

// Фоновый узор с иконками — как на карточках редактора (.cc-shell → ShellCover)
export const ShellCover = ({ id }) => (
  <svg aria-hidden="true" width="100%" height="100%" style={{ position: 'absolute', inset: 0, color: 'var(--cc-pat)', pointerEvents: 'none' }}>
    <defs>
      <pattern id={`csp-${id}`} width="130" height="130" patternUnits="userSpaceOnUse" patternTransform="rotate(-6)">
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.13">
          <g transform="translate(8,9)"><rect x="0" y="4" width="26" height="19" rx="4" /><path d="M0 10 h26" /></g>
          <g transform="translate(70,7)"><path d="M0 0 l14 8 l-14 8 z" /></g>
          <g transform="translate(102,10)"><circle cx="8" cy="8" r="8" /><path d="M13.7 13.7 l6 6" /></g>
          <g transform="translate(40,38)"><path d="M11 0 l11 6 v13 l-11 6 l-11 -6 v-13 z" /><path d="M0 6 l11 6 l11 -6 M11 12 v13" /></g>
          <g transform="translate(92,42)"><path d="M0 8 a14 14 0 0 1 20 0" /><path d="M4 13 a9 9 0 0 1 12 0" /><circle cx="10" cy="19" r="1.6" fill="currentColor" stroke="none" /></g>
          <g transform="translate(6,74)"><path d="M0 4 h9 l3 3 h14 a2.5 2.5 0 0 1 2.5 2.5 v11 a2.5 2.5 0 0 1 -2.5 2.5 h-26 a2.5 2.5 0 0 1 -2.5 -2.5 v-13.5 a2.5 2.5 0 0 1 2.5 -2.5 z" /></g>
          <g transform="translate(104,72)"><path d="M0 0 l6 5 l-6 5" /><path d="M10 12 h9" /></g>
          <g transform="translate(62,92)"><path d="M0 0 l0 22 l5 -6 l3.2 7.5 l3.4 -1.5 l-3.2 -7.5 l7.6 -0.8 z" /></g>
        </g>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#csp-${id})`} />
  </svg>
);

// Тон → hex (фикс. яркость/насыщенность) и hex → тон — для линии-ползунка
export const hueToHex = (h) => {
  const l = 0.62, a = Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.max(0, Math.min(255, Math.round(255 * v)));
  };
  return '#' + [f(0), f(8), f(4)].map(x => x.toString(16).padStart(2, '0')).join('');
};
export const hueOfHex = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || ''); const n = m ? parseInt(m[1], 16) : 0x7c4dff;
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (!d) return 0;
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60; if (h < 0) h += 360; return h;
};

const Switch = ({ on, onChange }) => (
  <button type="button" onClick={() => onChange(!on)} className={`cs2-sw${on ? ' on' : ''}`}><span /></button>
);

// Маленькое всплывающее окно у триггера (через портал — не режется скроллом модалки)
export function Popover({ anchorRef, open, onClose, width, children }) {
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    // Раскрываем вниз; если снизу не помещается — переворачиваем вверх, а если и
    // сверху мало места, прижимаем к краю и включаем прокрутку внутри окошка.
    const place = () => {
      const a = anchorRef.current; if (!a) return;
      const r = a.getBoundingClientRect();
      const h = popRef.current ? popRef.current.offsetHeight : 0;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const gap = 6, pad = 12;
      const below = window.innerHeight - r.bottom - gap - pad;
      const above = r.top - gap - pad;
      let top, maxH;
      if (!h || h <= below) { top = r.bottom + gap; maxH = below; }
      else if (h <= above) { top = r.top - gap - h; maxH = above; }
      else if (above > below) { top = pad; maxH = above; }
      else { top = r.bottom + gap; maxH = below; }
      setPos({ left, top: Math.max(pad, top), maxH: Math.max(120, maxH) });
    };
    place();
    // после первого рендера высота окошка известна — уточняем позицию
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, anchorRef, width]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, anchorRef, onClose]);
  if (!open) return null;
  return createPortal(
    <div ref={popRef} className="cs2-pop"
      style={{ position: 'fixed', width, zIndex: 1200,
        left: pos ? pos.left : -9999, top: pos ? pos.top : -9999,
        maxHeight: pos ? pos.maxH : undefined, overflowY: 'auto',
        visibility: pos ? 'visible' : 'hidden' }}>{children}</div>,
    document.body
  );
}

const CourseSettings = ({ course, onSaved, onClose }) => {
  /* Менеджер и куратор настройки только смотрят: нет сохранения и удаления,
     а поля отключены целиком — так новые настройки автоматически окажутся
     недоступны, про них не нужно вспоминать отдельно. */
  const { can } = useAuth();
  const ro = !can('courses.edit');
  const navigate = useNavigate();
  const [name, setName] = useState(course?.name || '');
  const [examType, setExamType] = useState(course?.examType || '');
  const [color, setColor] = useState(course?.color || '#7c4dff');
  const [icon, setIcon] = useState(course?.icon || 'zap');
  const [saving, setSaving] = useState(false);
  const [vis, setVis] = useState(course?.isActive === false ? 'draft' : 'pub');
  const [allIcons, setAllIcons] = useState(false);
  const [allCurators, setAllCurators] = useState(true);
  const [curator, setCurator] = useState('lapin');
  const [noCopy, setNoCopy] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const hueRef = useRef(null);
  const swatchRef = useRef(null);
  const allIconsRef = useRef(null);

  const save = async () => {
    if (!name.trim()) { toast.error('Введите название курса'); return; }
    setSaving(true);
    try {
      await updateCourse(course.id, { name: name.trim(), examType: examType || null, color, icon, isActive: vis === 'pub' });
      toast.success('Настройки сохранены');
      onSaved && onSaved();
      onClose && onClose();
    } catch { toast.error('Ошибка при сохранении'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog({ title: 'Удалить курс?', message: `«${course.name}» со всеми разделами, уроками и ДЗ будет удалён. Действие необратимо.`, confirmText: 'Удалить курс' }))) return;
    try { await deleteCourse(course.id); toast.success('Курс удалён'); navigate('/admin/dashboard'); }
    catch { toast.error('Ошибка при удалении'); }
  };

  const setHueFromX = (clientX) => {
    const el = hueRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setColor(hueToHex(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * 360));
  };
  const onHueDown = (e) => {
    e.preventDefault(); setHueFromX(e.clientX);
    const move = (ev) => setHueFromX(ev.clientX);
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  const suggested = ICONS.slice(0, 11);
  const segCls = (active) => `cs2-seg${active ? ' on' : ''}`;
  const huePos = `${(hueOfHex(color) / 360) * 100}%`;

  return (
    <div className="cs2-card">
      {/* ── Шапка (без аватарки курса) ── */}
      <div className="cs2-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cs2-title">Настройки курса</div>
          <div className="cs2-sub">{ro ? 'Только просмотр — менять курс может главный администратор' : 'Название, обложка, доступ и куратор курса'}</div>
        </div>
        {!ro && (
          <button className="cs2-save" onClick={save} disabled={saving}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        )}
        <button className="cs2-x" onClick={onClose} aria-label="Закрыть">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* ── Тело ── */}
      <fieldset disabled={ro} className="cs2-body" style={{ border: 0, minWidth: 0 }}>

        {/* Превью — прямоугольник с фоном-узором курса + белым аватаром */}
        <div className="cs2-preview">
          <ShellCover id={course?.id || 'x'} />
          <div className="cs2-ava"><CourseIcon icon={icon} size={34} sw={1.8} /></div>
          <div className="cs2-pv-info">
            <div className="cs2-pv-name">{name || 'Название курса'}</div>
            <div className="cs2-pv-badges">
              <span className="cs2-pv-pill"><span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />{examType || 'Курс'}</span>
              <span className="cs2-pv-pill" style={vis === 'pub' ? { background: 'rgba(34,197,94,0.16)', color: '#16a34a' } : { background: 'rgba(245,158,11,0.16)', color: '#d97706' }}>{vis === 'pub' ? 'Опубликован' : 'Черновик'}</span>
            </div>
          </div>
        </div>

        {/* Название */}
        <div className="cs2-field">
          <label className="cs2-lbl">Название курса</label>
          <input className="cs2-input" value={name} onChange={e => setName(e.target.value)} placeholder="Напр. ЕГЭ информатика 2026" />
        </div>

        {/* Тип экзамена + Видимость */}
        <div className="cs2-grid2">
          <div>
            <label className="cs2-lbl">Тип экзамена</label>
            <div className="cs2-segrow">
              {EXAMS.map(e => <button key={e.v} type="button" className={segCls(examType === e.v)} onClick={() => setExamType(e.v)}>{e.l}</button>)}
            </div>
          </div>
          <div>
            <label className="cs2-lbl">Видимость</label>
            <div className="cs2-segrow">
              {VIS.map(v => <button key={v.v} type="button" className={segCls(vis === v.v)} onClick={() => setVis(v.v)}>{v.l}</button>)}
            </div>
          </div>
        </div>

        {/* Цвет — линия-ползунок тона + hex */}
        <div className="cs2-field">
          <label className="cs2-lbl">Цвет плашки <span className="dim">— {color}</span></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button ref={swatchRef} type="button" className="cs2-swatch" style={{ background: color }} onClick={() => setShowPalette(p => !p)} title="Подобрать оттенок" aria-label="Подобрать оттенок" />
            <input className="cs2-hex" value={color} onChange={e => { let v = e.target.value.trim(); if (v && !v.startsWith('#')) v = '#' + v; setColor(v); }} />
          </div>
          <Popover anchorRef={swatchRef} open={showPalette} onClose={() => setShowPalette(false)} width={248}>
            <div className="cs2-hue" ref={hueRef} onMouseDown={onHueDown} title="Подобрать оттенок">
              <span className="cs2-hue-knob" style={{ left: huePos, background: color }} />
            </div>
          </Popover>
        </div>

        {/* Иконка: предложенные + «Все иконки» в той же строке */}
        <div className="cs2-field">
          <label className="cs2-lbl">Иконка</label>
          <div className="cs2-iconrow">
            {suggested.map(ic => (
              <button key={ic.key} type="button" title={ic.name} className={`cs2-iconcell${icon === ic.key ? ' on' : ''}`} onClick={() => setIcon(ic.key)}>
                <CourseIcon icon={ic.key} size={22} sw={1.7} />
              </button>
            ))}
            <button ref={allIconsRef} type="button" className="cs2-allicons" onClick={() => setAllIcons(a => !a)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
              Все иконки
            </button>
          </div>
          <Popover anchorRef={allIconsRef} open={allIcons} onClose={() => setAllIcons(false)} width={300}>
            <IconPicker value={icon} onChange={setIcon} />
          </Popover>
        </div>

        <div className="cs2-hr" />

        {/* Кураторы (демо) */}
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 15px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="cs2-rule-t">Проверяют все кураторы</div>
              <div className="cs2-rule-s">{allCurators ? 'Любой куратор может взять работу этого курса на проверку' : 'Работы уходят одному ответственному куратору'}</div>
            </div>
            <Switch on={allCurators} onChange={setAllCurators} />
          </div>
          {!allCurators && (
            <div style={{ padding: '0 15px 14px' }}>
              <div className="cs2-hr" style={{ margin: '0 0 13px' }} />
              <label className="cs2-lbl" style={{ fontSize: 12.5 }}>Кто проверяет ДЗ этого курса</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {CURATORS.map(c => (
                  <button key={c.key} type="button" className={`cs2-cur${curator === c.key ? ' on' : ''}`} onClick={() => setCurator(c.key)}>
                    <span className="cs2-cur-ava">{c.initials}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{c.role}</span>
                    </span>
                    <span className="cs2-dot">{curator === c.key && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Защита от копирования (демо) */}
        <div style={{ marginBottom: 18 }}>
          <div className="cs2-rule">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="cs2-rule-t">Защита от копирования текста</div>
              <div className="cs2-rule-s">Ученики не смогут выделить и скопировать условия заданий и материалы курса</div>
            </div>
            <Switch on={noCopy} onChange={setNoCopy} />
          </div>
        </div>

        {/* Удаление */}
        {!ro && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, paddingBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)', maxWidth: 340 }}>Удаление курса необратимо и снесёт все разделы, уроки и ДЗ.</span>
            <button className="cs2-del" onClick={remove}>Удалить курс</button>
          </div>
        )}
      </fieldset>

      {/* ── Футер ── */}
      <div className="cs2-foot">
        <span className="cs2-foot-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {ro ? 'Настройки курса доступны только для просмотра' : 'Ученики увидят изменения сразу после сохранения'}
        </span>
        <button className="cs2-close" onClick={onClose}>Закрыть</button>
      </div>
    </div>
  );
};

export default CourseSettings;
